import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Award,
  Clock,
  Target,
  User,
  Calendar,
  Briefcase,
  Globe,
  Loader2,
  ChevronRight,
  BookOpen,
  Brain,
  Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

// Alert box component
const AlertBox = ({ level, title, content }) => {
  const configs = {
    critical: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', icon: '🔴' },
    warning: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', icon: '🟠' },
    success: { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', icon: '🟢' },
    info: { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400', icon: '🔵' }
  };
  const config = configs[level] || configs.info;

  // Check if title already starts with an emoji to avoid duplication
  const titleStartsWithEmoji = title && /^[\u{1F300}-\u{1F9FF}]/u.test(title.trim());

  return (
    <div className={`${config.bg} ${config.border} border-2 rounded-lg p-4 my-4`} data-testid={`alert-${level}`}>
      {title && (
        <div className={`font-bold ${config.text} mb-2 flex items-center gap-2`}>
          {!titleStartsWithEmoji && <span>{config.icon}</span>}
          {title}
        </div>
      )}
      <div className={`${config.text}`}>{content}</div>
    </div>
  );
};

// Case details card
const CaseDetailsCard = ({ details }) => {
  if (!details || Object.keys(details).filter(k => details[k]).length === 0) return null;

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-600 my-4">
      <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
        <User className="w-5 h-5" />
        Customer Profile
      </h3>
      <div className="grid grid-cols-2 gap-4">
        {details.name && (
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-xs text-slate-400 block">Name</span>
              <span className="text-white font-medium">{details.name}</span>
            </div>
          </div>
        )}
        {details.nationality && (
          <div className="flex items-center gap-3">
            <Globe className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-xs text-slate-400 block">Nationality</span>
              <span className="text-white font-medium">{details.nationality}</span>
            </div>
          </div>
        )}
        {details.dob && (
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-xs text-slate-400 block">Date of Birth</span>
              <span className="text-white font-medium">{details.dob}</span>
            </div>
          </div>
        )}
        {details.occupation && (
          <div className="flex items-center gap-3">
            <Briefcase className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-xs text-slate-400 block">Occupation</span>
              <span className="text-white font-medium">{details.occupation}</span>
            </div>
          </div>
        )}
      </div>
      {details.context && (
        <p className="mt-4 text-slate-300 italic border-t border-slate-700 pt-4">
          {details.context}
        </p>
      )}
    </div>
  );
};

// Content block renderer
const ContentBlockRenderer = ({ block }) => {
  switch (block.block_type) {
    case 'text':
      return <p className="text-slate-300 my-3 whitespace-pre-wrap">{block.content}</p>;

    case 'alert':
      return <AlertBox level={block.alert_level} title={block.alert_title} content={block.content} />;

    case 'table':
      return (
        <div className="my-4 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {(block.table_headers || []).map((header, i) => (
                  <th key={i} className="bg-slate-700 text-amber-400 px-4 py-3 text-left border border-slate-600 font-semibold">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(block.table_rows || []).map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-slate-800' : 'bg-slate-800/50'}>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} className="px-4 py-3 text-slate-300 border border-slate-600">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'list':
      return (
        <ul className="my-4 space-y-2">
          {(block.list_items || []).map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-slate-300">
              <span className="text-amber-400 mt-1">•</span>
              {item}
            </li>
          ))}
        </ul>
      );

    default:
      return <p className="text-slate-300 my-3">{block.content}</p>;
  }
};

// Question component
const QuestionComponent = ({ question, onAnswer, selectedChoices, showResult, result }) => {
  const handleSelect = (choiceId) => {
    if (showResult) return;
    
    if (question.question_type === 'multiple') {
      const current = selectedChoices || [];
      if (current.includes(choiceId)) {
        onAnswer(current.filter(id => id !== choiceId));
      } else {
        onAnswer([...current, choiceId]);
      }
    } else {
      onAnswer([choiceId]);
    }
  };

  return (
    <div className="my-6">
      <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-amber-400" />
        {question.question_text}
      </h4>

      <div className="space-y-3">
        {question.choices.map((choice) => {
          const isSelected = (selectedChoices || []).includes(choice.choice_id);
          const isCorrect = choice.is_correct;
          
          let borderClass = 'border-slate-600 hover:border-slate-500';
          let bgClass = 'bg-slate-800';
          
          if (showResult) {
            if (isCorrect) {
              borderClass = 'border-green-500';
              bgClass = 'bg-green-500/20';
            } else if (isSelected && !isCorrect) {
              borderClass = 'border-red-500';
              bgClass = 'bg-red-500/20';
            }
          } else if (isSelected) {
            borderClass = 'border-amber-500';
            bgClass = 'bg-amber-500/20';
          }

          return (
            <button
              key={choice.choice_id}
              onClick={() => handleSelect(choice.choice_id)}
              disabled={showResult}
              data-testid={`choice-${choice.choice_id}`}
              className={`w-full text-left p-4 rounded-lg border-2 ${borderClass} ${bgClass} transition-all`}
            >
              <div className="flex items-start gap-3">
                <span className={`font-bold ${isSelected ? 'text-amber-400' : 'text-slate-400'}`}>
                  {choice.label})
                </span>
                <div className="flex-1">
                  <span className="text-white">{choice.text}</span>
                  {showResult && isSelected && choice.feedback && (
                    <p className={`mt-2 text-sm ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                      {choice.feedback}
                    </p>
                  )}
                </div>
                {showResult && (
                  <span>
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : isSelected ? (
                      <XCircle className="w-5 h-5 text-red-400" />
                    ) : null}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {showResult && question.explanation && (
        <div className="mt-4 p-4 bg-blue-500/20 border border-blue-500 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <span className="font-semibold text-blue-400">Explanation:</span>
              <p className="text-blue-200 mt-1">{question.explanation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Mental Model display
const MentalModelDisplay = ({ model }) => {
  if (!model) return null;

  return (
    <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-8 border border-amber-500/30">
      <h2 className="text-2xl font-bold text-amber-400 mb-6 flex items-center gap-3">
        <Brain className="w-7 h-7" />
        {model.title}
      </h2>
      
      <div className="space-y-4 mb-8">
        {(model.steps || []).map((step, i) => (
          <div key={i} className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center font-bold text-lg shrink-0">
              {step.number || i + 1}
            </div>
            <div>
              <h4 className="font-bold text-white">{step.title}</h4>
              <p className="text-slate-300 whitespace-pre-wrap">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {model.key_message && (
        <div className="bg-slate-900/50 rounded-lg p-4 border-l-4 border-amber-500">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-5 h-5 text-amber-400 mt-0.5" />
            <p className="text-amber-200 italic">{model.key_message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Player Component
export default function LearningLabPlayerV2() {
  const navigate = useNavigate();
  const { labId } = useParams();

  const [loading, setLoading] = useState(true);
  const [lab, setLab] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentScenarioIdx, setCurrentScenarioIdx] = useState(0);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [selectedChoices, setSelectedChoices] = useState({});
  const [questionResults, setQuestionResults] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finalResults, setFinalResults] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Load lab and start attempt
  useEffect(() => {
    const loadLab = async () => {
      try {
        // Start a new attempt
        const res = await axios.post(`${API}/learning-labs-v2/${labId}/start`, {}, { headers });
        setLab(res.data.lab);
        setAttempt(res.data.attempt);
      } catch (err) {
        console.error('Failed to load lab:', err);
        toast.error('Failed to load learning lab');
      } finally {
        setLoading(false);
      }
    };
    loadLab();
  }, [labId]);

  const currentScenario = lab?.scenarios?.[currentScenarioIdx];
  const currentStep = currentScenario?.steps?.[currentStepIdx];

  const totalSteps = lab?.scenarios?.reduce((acc, s) => acc + s.steps.length, 0) || 0;
  const completedSteps = lab?.scenarios?.slice(0, currentScenarioIdx).reduce((acc, s) => acc + s.steps.length, 0) + currentStepIdx;
  const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  const handleAnswerSelect = (questionId, choices) => {
    setSelectedChoices(prev => ({ ...prev, [questionId]: choices }));
  };

  const submitAnswer = async (question) => {
    const choices = selectedChoices[question.question_id] || [];
    if (choices.length === 0) {
      toast.error('Please select an answer');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/learning-labs-v2/${labId}/answer`, {
        attempt_id: attempt.attempt_id,
        scenario_id: currentScenario.scenario_id,
        step_id: currentStep.step_id,
        question_id: question.question_id,
        selected_choice_ids: choices
      }, { headers });

      setQuestionResults(prev => ({
        ...prev,
        [question.question_id]: res.data
      }));

      if (res.data.is_correct) {
        toast.success('Correct!');
      } else {
        toast.error('Not quite right');
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (!currentStep) return false;
    
    // For steps with questions, all must be answered
    if (currentStep.questions && currentStep.questions.length > 0) {
      return currentStep.questions.every(q => questionResults[q.question_id]);
    }
    
    return true;
  };

  const goToNext = async () => {
    // Check if there are more steps in current scenario
    if (currentStepIdx < currentScenario.steps.length - 1) {
      setCurrentStepIdx(prev => prev + 1);
      return;
    }

    // Check if there are more scenarios
    if (currentScenarioIdx < lab.scenarios.length - 1) {
      setCurrentScenarioIdx(prev => prev + 1);
      setCurrentStepIdx(0);
      return;
    }

    // Lab complete
    try {
      const res = await axios.post(`${API}/learning-labs-v2/${labId}/complete`, {
        attempt_id: attempt.attempt_id
      }, { headers });
      
      setFinalResults(res.data);
      setCompleted(true);
      toast.success('Lab completed!');
    } catch (err) {
      console.error('Failed to complete lab:', err);
      toast.error('Failed to complete lab');
    }
  };

  const goToPrevious = () => {
    if (currentStepIdx > 0) {
      setCurrentStepIdx(prev => prev - 1);
      return;
    }

    if (currentScenarioIdx > 0) {
      setCurrentScenarioIdx(prev => prev - 1);
      const prevScenario = lab.scenarios[currentScenarioIdx - 1];
      setCurrentStepIdx(prevScenario.steps.length - 1);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      </Layout>
    );
  }

  if (!lab) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <AlertTriangle className="w-12 h-12 mb-4" />
          <p>Learning lab not found</p>
          <Button onClick={() => navigate('/labs')} className="mt-4">
            Back to Labs
          </Button>
        </div>
      </Layout>
    );
  }

  // Completion screen
  if (completed) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 py-12">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
                finalResults?.passed ? 'bg-green-500/20' : 'bg-orange-500/20'
              }`}>
                {finalResults?.passed ? (
                  <Award className="w-12 h-12 text-green-400" />
                ) : (
                  <Target className="w-12 h-12 text-orange-400" />
                )}
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {finalResults?.passed ? 'Congratulations!' : 'Lab Completed'}
              </h1>
              <p className="text-slate-400">
                {finalResults?.passed 
                  ? 'You have successfully completed this learning lab.' 
                  : 'You have completed the lab. Review the material and try again to improve your score.'}
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Your Results</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-700 rounded-lg">
                  <div className="text-3xl font-bold text-amber-400">{Math.round(finalResults?.percentage || 0)}%</div>
                  <div className="text-sm text-slate-400">Score</div>
                </div>
                <div className="text-center p-4 bg-slate-700 rounded-lg">
                  <div className="text-3xl font-bold text-white">{finalResults?.total_score || 0}</div>
                  <div className="text-sm text-slate-400">Points Earned</div>
                </div>
                <div className="text-center p-4 bg-slate-700 rounded-lg">
                  <div className={`text-3xl font-bold ${finalResults?.passed ? 'text-green-400' : 'text-orange-400'}`}>
                    {finalResults?.passed ? 'PASS' : 'RETRY'}
                  </div>
                  <div className="text-sm text-slate-400">Status</div>
                </div>
              </div>
            </div>

            {/* Mental Model */}
            {lab.mental_model && <MentalModelDisplay model={lab.mental_model} />}

            <div className="flex justify-center gap-4 mt-8">
              <Button variant="outline" onClick={() => navigate('/labs')} className="border-slate-600">
                Back to Labs
              </Button>
              <Button onClick={() => window.location.reload()} className="bg-amber-500 hover:bg-amber-600">
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <Button variant="ghost" onClick={() => navigate('/labs')} className="text-slate-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Exit Lab
              </Button>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {lab.estimated_duration_minutes} min
                </span>
                <span className="flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  {lab.cpd_points} CPD
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">
                  Scenario {currentScenarioIdx + 1} of {lab.scenarios.length}: {currentScenario?.title}
                </span>
                <span className="text-amber-400">{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Scenario header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-amber-400 text-sm mb-2">
              <BookOpen className="w-4 h-4" />
              Step {currentStepIdx + 1} of {currentScenario?.steps?.length}
            </div>
            <h2 className="text-2xl font-bold text-white">{currentStep?.title}</h2>
          </div>

          {/* Case details if first step */}
          {currentStepIdx === 0 && currentScenario?.case_details && (
            <CaseDetailsCard details={currentScenario.case_details} />
          )}

          {/* Content blocks */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-6">
            {currentStep?.content_blocks?.map((block, i) => (
              <ContentBlockRenderer key={block.block_id || i} block={block} />
            ))}
          </div>

          {/* Questions */}
          {currentStep?.questions?.map((question) => (
            <div key={question.question_id} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-6">
              <QuestionComponent
                question={question}
                onAnswer={(choices) => handleAnswerSelect(question.question_id, choices)}
                selectedChoices={selectedChoices[question.question_id]}
                showResult={!!questionResults[question.question_id]}
                result={questionResults[question.question_id]}
              />
              {!questionResults[question.question_id] && (
                <Button
                  onClick={() => submitAnswer(question)}
                  disabled={submitting || !(selectedChoices[question.question_id]?.length > 0)}
                  data-testid="submit-answer-btn"
                  className="bg-amber-500 hover:bg-amber-600 mt-4"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Submit Answer
                </Button>
              )}
            </div>
          ))}

          {/* Learning points */}
          {currentStep?.learning_points?.length > 0 && questionResults[currentStep.questions?.[0]?.question_id] && (
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30 mb-6">
              <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Key Learning Points
              </h4>
              <ul className="space-y-2">
                {currentStep.learning_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-amber-200">
                    <CheckCircle className="w-4 h-4 mt-1 text-amber-400" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={goToPrevious}
              disabled={currentScenarioIdx === 0 && currentStepIdx === 0}
              data-testid="previous-btn"
              className="border-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            <Button
              onClick={goToNext}
              disabled={!canProceed()}
              data-testid="continue-btn"
              className="bg-amber-500 hover:bg-amber-600"
            >
              {currentScenarioIdx === lab.scenarios.length - 1 && currentStepIdx === currentScenario.steps.length - 1
                ? 'Complete Lab'
                : 'Continue'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
